import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Trash2, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ resident_id: '', amount: '', description: '', due_date: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [payRes, resRes] = await Promise.all([api.get('/admin/payments'), api.get('/admin/residents')]);
      setPayments(payRes.data);
      setResidents(resRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const createPayment = async () => {
    if (!form.resident_id || !form.amount || !form.description || !form.due_date) { toast.error('Fill all fields'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/payments', { ...form, amount: parseFloat(form.amount) });
      toast.success('Payment created');
      setShowNew(false);
      setForm({ resident_id: '', amount: '', description: '', due_date: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const markPaid = async (paymentId) => {
    try { await api.put(`/admin/payments/${paymentId}/mark-paid`); toast.success('Marked as paid'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const deletePayment = async (paymentId) => {
    try { await api.delete(`/admin/payments/${paymentId}`); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const filtered = payments.filter(p => {
    const matchSearch = p.resident_name?.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusBadge = (s) => {
    const map = { paid: 'bg-emerald-50 text-emerald-700 border-emerald-100', pending: 'bg-amber-50 text-amber-700 border-amber-100', overdue: 'bg-red-50 text-red-700 border-red-100' };
    return map[s] || 'bg-slate-100 text-slate-600';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;

  return (
    <div data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Payment Tracking</h1>
          <p className="text-sm text-slate-500 mt-1">{payments.length} payment records</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="new-payment-btn"><Plus size={14} /> Create Due</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-['Outfit']">Create Payment Due</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs mb-1 block">Resident</Label>
                <Select value={form.resident_id} onValueChange={v => setForm({...form, resident_id: v})}>
                  <SelectTrigger data-testid="payment-resident"><SelectValue placeholder="Select resident" /></SelectTrigger>
                  <SelectContent>
                    {residents.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name} ({r.unit_number})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs mb-1 block">Amount (INR)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="5000" data-testid="payment-amount" /></div>
              <div><Label className="text-xs mb-1 block">Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="e.g. Monthly Maintenance - March 2026" data-testid="payment-desc" /></div>
              <div><Label className="text-xs mb-1 block">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} data-testid="payment-due-date" /></div>
              <Button onClick={createPayment} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-payment-btn">
                {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Create Due
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search payments..." className="pl-9 text-sm" data-testid="payment-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[130px] text-sm" data-testid="payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Resident</TableHead>
                <TableHead className="text-xs">Description</TableHead>
                <TableHead className="text-xs">Amount</TableHead>
                <TableHead className="text-xs">Due Date</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">No payments found</TableCell></TableRow>
              )}
              {filtered.map(p => (
                <TableRow key={p.payment_id} data-testid={`payment-row-${p.payment_id}`}>
                  <TableCell>
                    <p className="text-sm font-medium text-slate-900">{p.resident_name}</p>
                    <p className="text-xs text-slate-500">{p.resident_email}</p>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{p.description}</TableCell>
                  <TableCell className="text-sm font-semibold text-slate-900">₹{p.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-slate-500">{p.due_date}</TableCell>
                  <TableCell><Badge className={`text-xs capitalize ${statusBadge(p.status)}`}>{p.status}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {p.status === 'pending' && (
                        <Button variant="ghost" size="sm" onClick={() => markPaid(p.payment_id)} className="text-emerald-600 hover:text-emerald-700 h-7 px-2 text-xs" data-testid={`mark-paid-${p.payment_id}`}>
                          <CheckCircle size={12} className="mr-1" /> Paid
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => deletePayment(p.payment_id)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`delete-payment-${p.payment_id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
